/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';
import Store from '../store/store';
import * as API from '../utils/api';
import Types from '../constants/actionTypes';
import { applicationError, notificationAction } from './notificationActions';
import { WorkPackageStatuses } from '../constants/storyStatusTypes';
import { fetchAllWorkPackages } from './workPackageActions';
import {getOrganizations} from "../utils/reduxHelpers";
import {fetchUsers} from "./userActions";

const dataFormatter = new Jsona();
const cadenceId = () => Store.getState().cadenceReducer.cadence?.id;

export const updateTargetStore = (target, action) => (dispatch) => {

  switch (action) {
    case 'update':
      return dispatch({ type: Types.UPDATE_TARGET, target });
    case 'create':
      return dispatch({ type: Types.ADD_TARGET, target });
    case 'delete':
      return dispatch({ type: Types.DELETE_TARGET, targetId: target });
  }
}

export const getTarget = (id) => (dispatch) => {
  dispatch({ type: Types.REQUEST_TARGET });
  return API.get(`targets/${id}`)
    .then((response) => {
      const target = response ? dataFormatter.deserialize(response) : {};
      const workPackages = target.work_packages || [];
      dispatch({ type: Types.SET_TARGET, target: target.id });
      dispatch({ type: Types.UPDATE_TARGET, target });
      dispatch({
        type: Types.UPDATE_MULTIPLE_WORK_PACKAGES,
        results: workPackages,
        cadenceId: cadenceId()
      });
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const setTarget = (id) => (dispatch) =>
  dispatch({ type: Types.SET_TARGET, target: id });

export const getAllTargets = () => (dispatch) => {
  dispatch({ type: Types.REQUEST_TARGET });
  return API.get('targets')
    .then((response) => {
      const targets = dataFormatter.deserialize(response);
      const workPackages = Store.getState().workPackageReducer.all;
      dispatch({ type: Types.RECEIVE_TARGETS, targets });
      if (workPackages.length > 0) {
        dispatch({
          workPackages,
          type: Types.ADD_WORK_PACKAGES_TO_TARGETS
        });
      }
    })
    .catch((error) => {
      dispatch(applicationError(error));
      dispatch({ type: Types.REQUEST_TARGET_FAILED });
    });
};

export const createTarget = (data) => (dispatch) => {
  dispatch({ type: Types.SEND_TARGET });
  return API.post('targets', data)
    .then(() => {
      dispatch(notificationAction(`Target was successfully created`,2));
      dispatch(getAllTargets());
      dispatch(fetchAllWorkPackages());
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const updateTarget = (id, data) => (dispatch) => {
  dispatch({ type: Types.SEND_TARGET });
  return API.put(`targets/${id}`, data)
    .then((response) => response.json())
    .then((responseData) => {
      const target = responseData
        ? dataFormatter.deserialize(responseData)
        : {};
      const workPackages = target.work_packages;
      dispatch({
        type: Types.UPDATE_MULTIPLE_WORK_PACKAGES,
        results: workPackages,
        cadenceId: cadenceId()
      });
      dispatch({ type: Types.UPDATE_TARGET, target });
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const updateTargetStatus = (id, status, data = {}) => (dispatch) => {
  dispatch({ type: Types.SEND_TARGET });
  const { targetToCarryOverId, unfinishedWorkPackageIds } = data;
  return API.post(`targets/${id}/change-status`, {
    status,
    unfinished_work_packages_ids: unfinishedWorkPackageIds,
    target_to_carry_over: targetToCarryOverId
  })
    .then((response) => response.json())
    .then((responseData) => {
      const target = responseData
        ? dataFormatter.deserialize(responseData)
        : {};
      const workPackages = target.work_packages;
      if (target.status === WorkPackageStatuses.ACCEPTED) {
        if (targetToCarryOverId > 0) {
          dispatch(getTarget(targetToCarryOverId));
        } else {
          dispatch(getAllTargets());
        }
      }
      if (
        status === WorkPackageStatuses.GETTING_READY &&
        target.status === WorkPackageStatuses.IN_PROGRESS
      ) {
        dispatch(
          notificationAction(`${target.short_name} has been started`,2)
        );
      }
      if (
        target.status === WorkPackageStatuses.LIVE &&
        target.status === WorkPackageStatuses.ACCEPTED
      ) {
        dispatch(
          notificationAction(`${target.short_name} has been completed`,2)
        );
      }
      dispatch({
        type: Types.UPDATE_MULTIPLE_WORK_PACKAGES,
        results: workPackages,
        cadenceId: cadenceId()
      });
      dispatch({ type: Types.UPDATE_TARGET, target });
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const deleteTarget = (targetId) => (dispatch) => {
  dispatch({ type: Types.SEND_TARGET });
  return API.del(`targets/${targetId}`)
    .then(() => dispatch(getAllTargets()))
    .catch((error) => dispatch(applicationError(error)));
};

export const restoreTarget = (targetId) => (dispatch) => {
  dispatch({ type: Types.SEND_TARGET });
  return API.put(`targets/${targetId}/restore`)
    .then(() => dispatch(getAllTargets()))
    .catch((error) => dispatch(applicationError(error)));
};

export function targetBurnup(targetId, storyCount) {
  const url = storyCount
    ? `targets/${targetId}/burnup-story-count`
    : `targets/${targetId}/burnup`;
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TARGET });
    return API.get(url)
      .then((response) =>
        dispatch({
          type: Types.GET_TARGET_BURNUP_DATA,
          burnupData: response
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function updateTargetBurnupData(graphData) {
  return (dispatch) =>
    dispatch({
      type: Types.GET_TARGET_BURNUP_DATA,
      burndownData: graphData
    });
}
